import { Component, OnInit, OnDestroy, ViewEncapsulation, Inject, Optional, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SubMenuService } from '../../../../services/submenu.service';
import {
    MapFilterService,
    MapFilterState,
    SavedMapFilter,
    WAYPOINT_ALERT_PILLS,
    WAYPOINT_BILLFISH_PILLS,
    WAYPOINT_EVENT_PILLS,
    WAYPOINT_INSHORE_TYPE_PILLS,
    WAYPOINT_PELAGIC_PILLS,
    WAYPOINT_PLACE_PILLS,
    WAYPOINT_TUNA_TYPE_PILLS,
} from '../../../../services/map-filter.service';
import { MapSkinService, MapSkin, MAP_SKIN_CATALOG } from '../../../../services/map-skin.service';
import { MatDatepickerModule, DateRange } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { SocialService } from '../../../../services/social.service';
import { AuthService } from '../../../../core/auth/auth.service';
import { TourAnchorDirective } from '../../../../features/learning-center/engine/tour-anchor.directive';

// A single Captain row in the roster — one person, all their id aliases collapsed.
interface CaptainRow {
    id: string;                 // canonical (lowercased) id
    allIds: Set<string>;        // every lowercased id alias this person appears under
    name: string;
    vesselName: string | null;
    avatarUrl: string | null;
    isDockmate: boolean;        // a direct connection
    groupIds: Set<string>;      // fleets this person belongs to
}

export interface PresetDef {
    id: string;
    label: string;
    subtitle?: string;
    scopesToFleet?: boolean;
    fleetIds?: string[];
    fleetNameMatch?: string;
}

@Component({
    selector: 'app-map-filter',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatIconModule,
        MatButtonModule,
        MatCheckboxModule,
        TourAnchorDirective
    ],
    templateUrl: './map-filter.component.html',
    styleUrl: './map-filter.component.scss',
    encapsulation: ViewEncapsulation.None
})
export class MapFilterComponent implements OnInit, OnDestroy {
    @Input() initialTab: 'filters' | 'styles' = 'filters';
    @Output() closed = new EventEmitter<any>();

    isOpen = false;
    private subs = new Subscription();

    // ── Mode: Picker vs Tuner (mirrors mobile app _FilterSheetMode) ────────────
    sheetMode: 'picker' | 'tuner' = 'picker';

    // ── Tabs ──────────────────────────────────────────────────────────────────
    activeDisplayTab: 'filters' | 'styles' = 'filters';
    tunerTab: 'when' | 'what' | 'who' = 'when';

    // ── Built-in Presets (mirrors mobile app kFleetScopedPresets & built-ins) ─
    readonly builtInPresets: PresetDef[] = [
        { id: 'today', label: 'Today', subtitle: 'Today · active & finished trips' },
        { id: 'live', label: 'Live trips', subtitle: 'Active trips only' },
        {
            id: 'waterproof_week',
            label: 'Waterproof this week',
            subtitle: 'Last 7 days · Waterproof Intel fleet',
            scopesToFleet: true,
            fleetIds: ['69a2dadc1081d247e2a9dec4'],
            fleetNameMatch: 'waterproof intel',
        },
        {
            id: 'pro_staff_week',
            label: 'Pro staff trips this week',
            subtitle: 'Last 7 days · Pro Staff fleet',
            scopesToFleet: true,
            fleetIds: ['6a89b14b015273f1c21373e6'],
            fleetNameMatch: 'pro staff',
        },
    ];

    selectedPresetId: string = 'today';
    savedFilters: SavedMapFilter[] = [];
    editingSavedId: string | null = null;

    // Save as filter modal
    showSaveModal = false;
    saveFilterInputName = '';

    // Map Skins
    readonly skinsCatalog = MAP_SKIN_CATALOG;
    selectedSkinId: string = MAP_SKIN_CATALOG.defaultSkin.id;

    get currentSkin(): MapSkin {
        return MAP_SKIN_CATALOG.byId(this.selectedSkinId);
    }

    filterForm: FormGroup;
    minDate = new Date(2020, 0, 1);
    maxDate = new Date();

    // ── Calendar state ────────────────────────────────────────────────────────
    showCalendar = false;
    selectedTrackLinesRange: DateRange<Date>;
    selectedWaypointsRange: DateRange<Date>;
    whenQuickPick: 'today' | '2days' | 'week' | 'month' | 'custom' = 'today';

    // ── What: Trips & Waypoints ───────────────────────────────────────────────
    tracklinesEnabled = true;
    fadeOldTracklines = true;
    activeTripsOnly = false;
    tripsExpanded = true;

    waypointsEnabled = true;
    eventsExpanded = true;
    expandedFishCategories = new Set<string>(['Tuna Caught']);
    placesExpanded = true;
    alertsExpanded = true;

    // ── Who: Visibility tab — Sources ─────────────────────────────────────────
    mineEnabled = true;
    sharedEnabled = true;
    publicEnabled = true;

    // ── Groups (fleets) ───────────────────────────────────────────────────────
    fleets: any[] = [];
    loadingFleets = false;
    expandFleets = false;
    groupSearchQuery = '';

    // ── Captains roster ───────────────────────────────────────────────────────
    expandCaptains = false;
    captainSearchQuery = '';
    roster: CaptainRow[] = [];
    loadingRoster = false;

    private dockmates: any[] = [];
    private fleetMembersByFleetId: Record<string, any[]> = {};
    private feedCaptains: any[] = [];

    // ── Cascade state (source of truth) ───────────────────────────────────────
    hiddenGroupIds = new Set<string>();
    hiddenCaptainUserIds = new Set<string>();
    shownCaptainUserIds = new Set<string>();
    captainDisplayNames: Record<string, string> = {};
    private autoSeededHiddenGroups = new Set<string>();

    // ── Catalogs ──────────────────────────────────────────────────────────────
    readonly eventPills       = WAYPOINT_EVENT_PILLS;
    readonly tunaTypePills    = WAYPOINT_TUNA_TYPE_PILLS;
    readonly billfishPills    = WAYPOINT_BILLFISH_PILLS;
    readonly pelagicPills     = WAYPOINT_PELAGIC_PILLS;
    readonly inshoreTypePills = WAYPOINT_INSHORE_TYPE_PILLS;
    readonly placePills       = WAYPOINT_PLACE_PILLS;
    readonly alertPills       = WAYPOINT_ALERT_PILLS;

    readonly eventIcons: Record<string, string> = {
        'Tuna Caught':    'phishing',
        'Billfish Caught':'phishing',
        'Pelagic Caught': 'phishing',
        'Inshore Caught': 'phishing',
        'Fish Mark':      'set_meal',
        'Bait Mark':      'rice_bowl',
        'Temp Break':     'thermostat'
    };
    readonly placeIcons: Record<string, string> = {
        Spot: 'close', Wreck: 'dangerous', Inlet: 'water', Anchorage: 'anchor', Home: 'home'
    };
    readonly alertIcons: Record<string, string> = {
        Hazard: 'warning_amber', Diver: 'pool', Patrol: 'local_police', Fog: 'cloud'
    };

    selectedEvents    = new Set(this.eventPills);
    selectedTunaTypes = new Set(this.tunaTypePills);
    selectedBillfish  = new Set(this.billfishPills);
    selectedPelagic   = new Set(this.pelagicPills);
    selectedInshore   = new Set(this.inshoreTypePills);
    selectedPlaces    = new Set(this.placePills);
    selectedAlerts    = new Set(this.alertPills);

    constructor(
        private fb: FormBuilder,
        private mapFilterService: MapFilterService,
        private mapSkinService: MapSkinService,
        private socialService: SocialService,
        private authService: AuthService,
        private submenuService: SubMenuService,
        @Optional() private dialogRef?: MatDialogRef<MapFilterComponent>,
        @Optional() @Inject(MAT_DIALOG_DATA) private data?: any
    ) {
        if (this.dialogRef) {
            this.isOpen = true;
        }
        this.selectedSkinId = this.mapSkinService.currentSkinId;
        if (this.data?.initialTab === 'styles') {
            this.activeDisplayTab = 'styles';
        }
        this.feedCaptains = (this.data?.visibleCaptains ?? []) as any[];

        // ── Restore state from the service ────────────────────────────────────
        const s = this.mapFilterService.getFilterState();

        this.selectedTrackLinesRange = new DateRange<Date>(s.trackLinesDateRange.start, s.trackLinesDateRange.end);
        this.selectedWaypointsRange  = new DateRange<Date>(s.waypointsDateRange.start, s.waypointsDateRange.end);

        this.tracklinesEnabled = s.tracklinesEnabled ?? true;
        this.waypointsEnabled  = s.waypointsEnabled  ?? true;
        this.fadeOldTracklines = s.fadeOldTracklines  ?? true;
        this.publicEnabled     = s.publicEnabled      ?? true;
        this.activeTripsOnly   = s.activeTripsOnly    ?? false;

        this.mineEnabled   = s.selectedUsers.mine ?? true;
        this.sharedEnabled = true;

        this.hiddenGroupIds        = new Set((s.hiddenGroupIds ?? []).map((id) => this.norm(id)));
        this.hiddenCaptainUserIds  = new Set((s.hiddenCaptainUserIds ?? []).map((id) => this.norm(id)));
        this.shownCaptainUserIds   = new Set((s.shownCaptainUserIds ?? []).map((id) => this.norm(id)));
        this.captainDisplayNames   = { ...(s.captainDisplayNames ?? {}) };

        this.selectedEvents    = s.selectedEvents    ? new Set(s.selectedEvents)    : new Set(this.eventPills);
        this.selectedTunaTypes = s.selectedTunaTypes  ? new Set(s.selectedTunaTypes) : new Set(this.tunaTypePills);
        this.selectedBillfish  = s.selectedBillfish   ? new Set(s.selectedBillfish)  : new Set(this.billfishPills);
        this.selectedPelagic   = s.selectedPelagic    ? new Set(s.selectedPelagic)   : new Set(this.pelagicPills);
        this.selectedInshore   = s.selectedInshore    ? new Set(s.selectedInshore)   : new Set(this.inshoreTypePills);
        this.selectedPlaces    = s.selectedPlaces     ? new Set(s.selectedPlaces)    : new Set(this.placePills);
        this.selectedAlerts    = s.selectedAlerts     ? new Set(s.selectedAlerts)    : new Set(this.alertPills);

        this.filterForm = this.fb.group({
            users: this.fb.group({ mine: [this.mineEnabled], shared: [this.sharedEnabled] })
        });

        this.savedFilters = this.mapFilterService.getSavedFilters();
        this.detectActivePreset();
        this.rebuildRoster();
    }

    ngOnInit() {
        if (this.initialTab) {
            this.activeDisplayTab = this.initialTab;
        }
        this.subs.add(
            this.submenuService.filterOpenState$.subscribe((state) => {
                this.isOpen = state;
                if (state) {
                    const s = this.mapFilterService.getFilterState();
                    this.loadFromState(s);
                    this.detectActivePreset();
                    this.savedFilters = this.mapFilterService.getSavedFilters();
                    this.rebuildRoster();
                }
            })
        );
        this.subs.add(
            this.submenuService.filterModalTab$.subscribe((tab) => {
                this.activeDisplayTab = tab;
            })
        );
        this.subs.add(
            this.mapFilterService.savedFilters$.subscribe((list) => {
                this.savedFilters = list;
            })
        );
        this.loadRosterData();
    }

    ngOnDestroy() {
        this.subs.unsubscribe();
    }

    // ── Active preset label getter ────────────────────────────────────────────
    get activePresetName(): string {
        const foundBuiltIn = this.builtInPresets.find(p => p.id === this.selectedPresetId);
        if (foundBuiltIn) return foundBuiltIn.label;
        return 'Today';
    }

    get isCustomPreset(): boolean {
        return false;
    }

    // ── Mode toggles ──────────────────────────────────────────────────────────
    openTuner() {
        this.sheetMode = 'tuner';
    }

    backToPicker() {
        this.persist(false);
        this.sheetMode = 'picker';
    }

    switchDisplayTab(tab: 'filters' | 'styles'): void {
        this.activeDisplayTab = tab;
    }

    switchTunerTab(tab: 'when' | 'what' | 'who'): void {
        this.tunerTab = tab;
    }

    // ── Preset Selection & Application ────────────────────────────────────────
    applyPreset(presetId: string): void {
        this.selectedPresetId = presetId;
        this.editingSavedId = null;

        const now = new Date();
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
        const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);

        if (presetId === 'today') {
            this.activeTripsOnly = false;
            this.selectedTrackLinesRange = new DateRange<Date>(todayStart, todayEnd);
            this.selectedWaypointsRange = new DateRange<Date>(todayStart, todayEnd);
            this.mineEnabled = true;
            this.publicEnabled = true;
            this.hiddenGroupIds.clear();
            this.hiddenCaptainUserIds.clear();
            this.shownCaptainUserIds.clear();
            this.whenQuickPick = 'today';
        } else if (presetId === 'live') {
            this.activeTripsOnly = true;
            this.selectedTrackLinesRange = new DateRange<Date>(todayStart, todayEnd);
            this.selectedWaypointsRange = new DateRange<Date>(todayStart, todayEnd);
            this.mineEnabled = true;
            this.publicEnabled = true;
            this.hiddenGroupIds.clear();
            this.hiddenCaptainUserIds.clear();
            this.shownCaptainUserIds.clear();
            this.whenQuickPick = 'today';
        } else if (presetId === 'waterproof_week') {
            const weekAgo = new Date(todayStart);
            weekAgo.setDate(weekAgo.getDate() - 6);
            this.activeTripsOnly = false;
            this.selectedTrackLinesRange = new DateRange<Date>(weekAgo, todayEnd);
            this.selectedWaypointsRange = new DateRange<Date>(weekAgo, todayEnd);
            this.mineEnabled = true;
            this.publicEnabled = false;
            this.whenQuickPick = 'week';
            this.scopeToFleetSpec(this.builtInPresets.find(p => p.id === 'waterproof_week')!);
        } else if (presetId === 'pro_staff_week') {
            const weekAgo = new Date(todayStart);
            weekAgo.setDate(weekAgo.getDate() - 6);
            this.activeTripsOnly = false;
            this.selectedTrackLinesRange = new DateRange<Date>(weekAgo, todayEnd);
            this.selectedWaypointsRange = new DateRange<Date>(weekAgo, todayEnd);
            this.mineEnabled = true;
            this.publicEnabled = false;
            this.whenQuickPick = 'week';
            this.scopeToFleetSpec(this.builtInPresets.find(p => p.id === 'pro_staff_week')!);
        } else {
            // Check saved filters
            const saved = this.savedFilters.find(f => f.id === presetId);
            if (saved) {
                this.editingSavedId = saved.id;
                this.loadFromState(saved.state);
            }
        }

        this.persist(false);
    }

    private scopeToFleetSpec(spec: PresetDef) {
        this.hiddenGroupIds.clear();
        this.hiddenCaptainUserIds.clear();
        const targetIds = new Set((spec.fleetIds || []).map(id => this.norm(id)));
        const matchName = (spec.fleetNameMatch || '').toLowerCase();

        this.fleets.forEach(f => {
            const cands = this.getFleetIdCandidates(f);
            const name = (f?.name || '').toLowerCase();
            const isMatch = cands.some(id => targetIds.has(id)) || (matchName && name.includes(matchName));
            if (!isMatch) {
                cands.forEach(id => this.hiddenGroupIds.add(id));
            }
        });
        this.hiddenGroupIds = new Set(this.hiddenGroupIds);
    }

    private loadFromState(s: MapFilterState) {
        this.selectedTrackLinesRange = new DateRange<Date>(s.trackLinesDateRange.start, s.trackLinesDateRange.end);
        this.selectedWaypointsRange  = new DateRange<Date>(s.waypointsDateRange.start, s.waypointsDateRange.end);
        this.tracklinesEnabled = s.tracklinesEnabled ?? true;
        this.waypointsEnabled  = s.waypointsEnabled  ?? true;
        this.fadeOldTracklines = s.fadeOldTracklines  ?? true;
        this.publicEnabled     = s.publicEnabled      ?? true;
        this.activeTripsOnly   = s.activeTripsOnly    ?? false;
        this.mineEnabled       = s.selectedUsers.mine ?? true;

        this.hiddenGroupIds       = new Set((s.hiddenGroupIds ?? []).map(id => this.norm(id)));
        this.hiddenCaptainUserIds = new Set((s.hiddenCaptainUserIds ?? []).map(id => this.norm(id)));
        this.shownCaptainUserIds  = new Set((s.shownCaptainUserIds ?? []).map(id => this.norm(id)));
        this.captainDisplayNames  = { ...(s.captainDisplayNames ?? {}) };

        this.selectedEvents    = s.selectedEvents ? new Set(s.selectedEvents) : new Set(this.eventPills);
        this.selectedTunaTypes = s.selectedTunaTypes ? new Set(s.selectedTunaTypes) : new Set(this.tunaTypePills);
        this.selectedBillfish  = s.selectedBillfish ? new Set(s.selectedBillfish) : new Set(this.billfishPills);
        this.selectedPelagic   = s.selectedPelagic ? new Set(s.selectedPelagic) : new Set(this.pelagicPills);
        this.selectedInshore   = s.selectedInshore ? new Set(s.selectedInshore) : new Set(this.inshoreTypePills);
        this.selectedPlaces    = s.selectedPlaces ? new Set(s.selectedPlaces) : new Set(this.placePills);
        this.selectedAlerts    = s.selectedAlerts ? new Set(s.selectedAlerts) : new Set(this.alertPills);
    }

    private detectActivePreset() {
        if (this.activeTripsOnly) {
            this.selectedPresetId = 'live';
            this.whenQuickPick = 'today';
            return;
        }
        const s = this.selectedTrackLinesRange?.start;
        const e = this.selectedTrackLinesRange?.end;
        if (!s || !e) {
            this.selectedPresetId = 'today';
            this.whenQuickPick = 'today';
            return;
        }

        const startDay = new Date(s.getFullYear(), s.getMonth(), s.getDate()).getTime();
        const endDay = new Date(e.getFullYear(), e.getMonth(), e.getDate()).getTime();
        const diffDays = Math.round((endDay - startDay) / (1000 * 60 * 60 * 24));

        if (diffDays <= 0) {
            this.selectedPresetId = 'today';
            this.whenQuickPick = 'today';
        } else if (diffDays <= 2) {
            this.selectedPresetId = 'today';
            this.whenQuickPick = '2days';
        } else if (diffDays <= 7) {
            this.whenQuickPick = 'week';
            if (this.hiddenGroupIds.size > 0 && !this.publicEnabled) {
                this.selectedPresetId = 'waterproof_week';
            } else {
                this.selectedPresetId = 'today';
            }
        } else if (diffDays <= 31) {
            this.whenQuickPick = 'month';
            this.selectedPresetId = 'today';
        } else {
            this.whenQuickPick = 'custom';
            this.selectedPresetId = 'today';
        }
    }

    // ── When Quick Picks ──────────────────────────────────────────────────────
    applyWhenQuickPick(pick: 'today' | '2days' | 'week' | 'month') {
        this.whenQuickPick = pick;
        const now = new Date();
        const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);

        if (pick === 'today') {
            const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
            this.selectedTrackLinesRange = new DateRange<Date>(start, end);
            this.selectedWaypointsRange = new DateRange<Date>(start, end);
            this.activeTripsOnly = false;
        } else if (pick === '2days') {
            const start = new Date(now.getTime() - 48 * 60 * 60 * 1000);
            this.selectedTrackLinesRange = new DateRange<Date>(start, end);
            this.selectedWaypointsRange = new DateRange<Date>(start, end);
            this.activeTripsOnly = false;
        } else if (pick === 'week') {
            const start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            this.selectedTrackLinesRange = new DateRange<Date>(start, end);
            this.selectedWaypointsRange = new DateRange<Date>(start, end);
            this.activeTripsOnly = false;
        } else if (pick === 'month') {
            const start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            this.selectedTrackLinesRange = new DateRange<Date>(start, end);
            this.selectedWaypointsRange = new DateRange<Date>(start, end);
            this.activeTripsOnly = false;
        }
        this.persist(false);
    }

    // ── Calendar Range ────────────────────────────────────────────────────────
    get activeRange(): DateRange<Date> { return this.selectedTrackLinesRange; }

    formatRange(r: DateRange<Date>): string {
        const s = r?.start;
        const e = r?.end || s;
        if (!s) return 'Select date range';
        const sStr = s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        const eStr = e ? e.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : sStr;
        return `${sStr} – ${eStr}`;
    }

    getFullDateRange(): string {
        return this.formatRange(this.selectedTrackLinesRange);
    }

    onDateSelected(date: Date) {
        const cur = this.selectedTrackLinesRange;
        let next: DateRange<Date>;
        if (!cur.start || cur.end) {
            next = new DateRange<Date>(date, null);
        } else {
            next = date < cur.start ? new DateRange<Date>(date, cur.start) : new DateRange<Date>(cur.start, date);
        }
        this.selectedTrackLinesRange = next;
        this.selectedWaypointsRange  = next;
        this.whenQuickPick = 'custom';
        this.activeTripsOnly = false;
    }

    openCalendar() {
        this.showCalendar = true;
        this.activeTripsOnly = false;
        this.whenQuickPick = 'custom';
    }

    closeCalendar() { this.showCalendar = false; }
    saveCalendar() {
        this.showCalendar = false;
        this.persist(false);
    }

    // ── What: Trips & Waypoints Controls ─────────────────────────────────────
    toggleAllTripsCompleted() {
        this.activeTripsOnly = !this.activeTripsOnly;
        this.persist(false);
    }

    toggleFishCategory(catKey: string) {
        if (this.expandedFishCategories.has(catKey)) {
            this.expandedFishCategories.delete(catKey);
        } else {
            this.expandedFishCategories.add(catKey);
        }
    }

    toggleAllEvents(): void {
        if (this.selectedEvents.size === this.eventPills.length) {
            this.selectedEvents.clear();
        } else {
            this.selectedEvents = new Set(this.eventPills);
        }
        this.persist(false);
    }

    togglePill(set: Set<string>, item: string) {
        set.has(item) ? set.delete(item) : set.add(item);
        this.persist(false);
    }

    // ── Save as filter modal & persistence ───────────────────────────────────
    openSaveModal() {
        this.saveFilterInputName = this.activePresetName === 'Custom' ? 'My Custom Filter' : `${this.activePresetName} (Custom)`;
        this.showSaveModal = true;
    }

    closeSaveModal() {
        this.showSaveModal = false;
    }

    confirmSaveFilter() {
        const name = this.saveFilterInputName.trim() || 'My Filter';
        this.persist(false);
        const currentState = this.mapFilterService.getFilterState();
        const saved = this.mapFilterService.saveFilter(name, currentState);
        this.selectedPresetId = saved.id;
        this.editingSavedId = saved.id;
        this.showSaveModal = false;
    }

    deleteCurrentSavedFilter() {
        if (!this.editingSavedId) return;
        this.mapFilterService.deleteSavedFilter(this.editingSavedId);
        this.editingSavedId = null;
        this.applyPreset('today');
        this.sheetMode = 'picker';
    }

    // ── Reset Filter ─────────────────────────────────────────────────────────
    resetFilters() {
        this.applyPreset('today');
        this.whenQuickPick = 'today';
        this.groupSearchQuery = '';
        this.captainSearchQuery = '';
        this.selectedEvents    = new Set(this.eventPills);
        this.selectedTunaTypes = new Set(this.tunaTypePills);
        this.selectedBillfish  = new Set(this.billfishPills);
        this.selectedPelagic   = new Set(this.pelagicPills);
        this.selectedInshore   = new Set(this.inshoreTypePills);
        this.selectedPlaces    = new Set(this.placePills);
        this.selectedAlerts    = new Set(this.alertPills);
        this.persist(true);
    }

    // ── Map Skin Selection ───────────────────────────────────────────────────
    selectSkin(skinId: string): void {
        this.selectedSkinId = skinId;
        this.mapSkinService.selectSkin(skinId);
    }

    resetSkin(): void {
        this.selectedSkinId = MAP_SKIN_CATALOG.defaultSkin.id;
        this.mapSkinService.resetSkin();
    }

    // ── Apply & Close ────────────────────────────────────────────────────────
    applyFilter() {
        this.persist(false);
        const state = this.mapFilterService.getFilterState();
        this.isOpen = false;
        this.submenuService.setFilterOpenState(false);
        this.closed.emit(state);
        if (this.dialogRef) {
            this.dialogRef.close(state);
        }
    }

    close() {
        this.isOpen = false;
        this.submenuService.setFilterOpenState(false);
        this.closed.emit();
        if (this.dialogRef) {
            this.dialogRef.close();
        }
    }

    private persist(asDefault: boolean) {
        this.filterForm.patchValue({ users: { mine: this.mineEnabled, shared: true } });
        const newState: MapFilterState = {
            trackLinesDateRange: { start: this.selectedTrackLinesRange.start, end: this.selectedTrackLinesRange.end },
            waypointsDateRange:  { start: this.selectedTrackLinesRange.start, end: this.selectedTrackLinesRange.end },
            selectedUsers: { mine: this.mineEnabled, shared: true },
            waypointsEnabled: this.waypointsEnabled,
            tracklinesEnabled: this.tracklinesEnabled,
            fadeOldTracklines: this.fadeOldTracklines,
            publicEnabled: this.publicEnabled,
            activeTripsOnly: this.activeTripsOnly,
            hiddenFleetIds: [...this.hiddenGroupIds],
            hiddenFleetMemberIds: [...this.hiddenCaptainUserIds],
            hiddenDockmateIds: [...this.hiddenCaptainUserIds],
            hiddenGroupIds: [...this.hiddenGroupIds],
            hiddenCaptainUserIds: [...this.hiddenCaptainUserIds],
            shownCaptainUserIds: [...this.shownCaptainUserIds],
            connectionUserIds: asDefault ? [] : [...this.connectionUserIds()],
            captainDisplayNames: { ...this.captainDisplayNames },
            selectedEvents:    [...this.selectedEvents],
            selectedTunaTypes: [...this.selectedTunaTypes],
            selectedBillfish:  [...this.selectedBillfish],
            selectedPelagic:   [...this.selectedPelagic],
            selectedInshore:   [...this.selectedInshore],
            selectedPlaces:    [...this.selectedPlaces],
            selectedAlerts:    [...this.selectedAlerts],
        };
        this.mapFilterService.setFilterState(newState);
    }

    // ── Roster & Group Helpers ───────────────────────────────────────────────
    private norm(v: any): string { return String(v ?? '').trim().toLowerCase(); }

    private dockmateIds(dm: any): Set<string> {
        const u = dm?.user || dm || {};
        return new Set([u._id, u.id, u.user_id, u.identifier, u.odl_user_id, dm?._id, dm?.id]
            .map((x) => this.norm(x)).filter(Boolean));
    }
    private fleetMemberIds(m: any): Set<string> {
        const u = m?.user || m || {};
        return new Set([u._id, u.id, u.user_id, u.identifier, u.profile_id, m?.user_id, m?._id, m?.id]
            .map((x) => this.norm(x)).filter(Boolean));
    }

    getFleetId(f: any): string { return this.norm(f?.identifier || f?._id || f?.id || f?.community_id); }
    private getFleetIdCandidates(f: any): string[] {
        return [f?.identifier, f?._id, f?.id, f?.community_id].map((v) => this.norm(v)).filter(Boolean);
    }
    getInitials(name: string): string { return (name || '?')[0].toUpperCase(); }

    private loadRosterData() {
        this.loadingRoster = true;
        this.loadingFleets = true;
        forkJoin({
            dockmates: this.socialService.myDockmates(1, 200).pipe(catchError(() => of({ results: [] }))),
            fleets: this.socialService.myFleets(1, 10).pipe(catchError(() => of({ results: [] }))),
        }).subscribe(({ dockmates, fleets }: any) => {
            this.dockmates = dockmates?.results || dockmates || [];
            this.fleets = fleets?.results || fleets || [];
            this.loadingFleets = false;
            this.loadingRoster = false;
            this.rebuildRoster();
            this.loadAllFleetMembers();
        });
    }

    private loadAllFleetMembers() {
        this.fleets.forEach((f) => {
            const communityId = (f?.identifier || f?._id || f?.id || '').toString().trim();
            const key = this.getFleetId(f);
            if (!communityId || this.fleetMembersByFleetId[key]) return;
            this.socialService.getCommunityMembers(communityId, 1, 100)
                .pipe(catchError(() => of({ results: [] })))
                .subscribe((res: any) => {
                    this.fleetMembersByFleetId[key] = res?.results || res || [];
                    this.rebuildRoster();
                });
        });
    }

    private rebuildRoster() {
        const myIds = new Set([
            this.norm(this.authService.getUserObjectId()),
            this.norm(this.authService.getUserIdFromToken()),
        ].filter(Boolean));

        const byCanonical: Record<string, CaptainRow> = {};
        const aliasToCanonical: Record<string, string> = {};

        const mergeRow = (rawIds: Set<string>, opts: { name?: string; vessel?: string; avatar?: string; isDockmate?: boolean; groupId?: string }) => {
            const ids = new Set([...rawIds].map((e) => this.norm(e)).filter(Boolean));
            if (ids.size === 0 || [...ids].some((id) => myIds.has(id))) return;

            let canonical: string | undefined;
            for (const id of ids) { const c = aliasToCanonical[id]; if (c) { canonical = c; break; } }
            if (!canonical) canonical = [...ids][0];

            const prev = byCanonical[canonical];
            const allIds = new Set([...(prev?.allIds ?? []), ...ids]);
            allIds.forEach((id) => { aliasToCanonical[id] = canonical!; });

            const groups = new Set([...(prev?.groupIds ?? [])]);
            if (opts.groupId && opts.groupId.trim()) groups.add(opts.groupId.trim());

            byCanonical[canonical] = {
                id: canonical,
                allIds,
                name: (opts.name && opts.name.trim()) ? opts.name.trim() : (prev?.name ?? ''),
                vesselName: (opts.vessel && opts.vessel.trim()) ? opts.vessel.trim() : (prev?.vesselName ?? null),
                avatarUrl: (opts.avatar && opts.avatar.trim()) ? opts.avatar.trim() : (prev?.avatarUrl ?? null),
                isDockmate: !!opts.isDockmate || (prev?.isDockmate ?? false),
                groupIds: groups,
            };
        };

        for (const dm of this.dockmates) {
            const u = dm?.user || dm || {};
            mergeRow(this.dockmateIds(dm), {
                name: u.name || u.full_name || u.username || u.display_name,
                vessel: dm.vessel_name || u.vessel_name,
                avatar: u.image_url || u.avatar_url,
                isDockmate: true,
            });
        }
        for (const fid of Object.keys(this.fleetMembersByFleetId)) {
            for (const m of this.fleetMembersByFleetId[fid]) {
                const u = m?.user || m || {};
                mergeRow(this.fleetMemberIds(m), {
                    name: u.name || u.display_name || u.full_name,
                    vessel: m.vessel_name || u.vessel_name,
                    avatar: u.image_url || u.avatar_url,
                    groupId: fid,
                });
            }
        }
        for (const fc of this.feedCaptains) {
            mergeRow(new Set([this.norm(fc.id)]), { name: fc.name, vessel: fc.vessel_name, avatar: fc.image_url });
        }
        for (const id of this.hiddenCaptainUserIds) {
            if (aliasToCanonical[id]) continue;
            mergeRow(new Set([id]), { name: this.captainDisplayNames[id] });
        }

        const rows = Object.values(byCanonical);
        rows.sort((a, b) => (a.name || '~').toLowerCase().localeCompare((b.name || '~').toLowerCase()));
        this.roster = rows;

        for (const fid of this.hiddenGroupIds) {
            if (this.autoSeededHiddenGroups.has(fid)) continue;
            const members = rows.filter((r) => r.groupIds.has(fid));
            if (members.length === 0) continue;
            for (const row of members) {
                if ([...row.allIds].some((id) => this.shownCaptainUserIds.has(id))) continue;
                this.setCaptainHidden(row, true, false);
            }
            this.autoSeededHiddenGroups.add(fid);
        }
    }

    private connectionUserIds(): Set<string> {
        const ids = new Set<string>();
        for (const dm of this.dockmates) this.dockmateIds(dm).forEach((id) => ids.add(id));
        for (const fid of Object.keys(this.fleetMembersByFleetId)) {
            for (const m of this.fleetMembersByFleetId[fid]) this.fleetMemberIds(m).forEach((id) => ids.add(id));
        }
        return ids;
    }

    private setCaptainHidden(row: CaptainRow, hide: boolean, userInitiated: boolean) {
        if (hide) {
            row.allIds.forEach((id) => this.hiddenCaptainUserIds.add(id));
            row.allIds.forEach((id) => this.shownCaptainUserIds.delete(id));
            if (row.name) row.allIds.forEach((id) => { this.captainDisplayNames[id] = row.name; });
        } else {
            row.allIds.forEach((id) => this.hiddenCaptainUserIds.delete(id));
            if (userInitiated) {
                row.allIds.forEach((id) => this.shownCaptainUserIds.add(id));
            } else {
                row.allIds.forEach((id) => this.shownCaptainUserIds.delete(id));
            }
            row.allIds.forEach((id) => { delete this.captainDisplayNames[id]; });
        }
        this.hiddenCaptainUserIds = new Set(this.hiddenCaptainUserIds);
        this.shownCaptainUserIds = new Set(this.shownCaptainUserIds);
    }

    captainChecked(row: CaptainRow): boolean {
        return ![...row.allIds].some((id) => this.hiddenCaptainUserIds.has(id));
    }

    toggleCaptain(row: CaptainRow) {
        this.setCaptainHidden(row, this.captainChecked(row), true);
        this.persist(false);
    }

    private static readonly CAPTAIN_PREVIEW_COUNT = 8;

    get filteredRoster(): CaptainRow[] {
        const q = this.captainSearchQuery.trim().toLowerCase();
        if (!q) return this.roster;
        return this.roster.filter((r) =>
            (r.name || '').toLowerCase().includes(q) || (r.vesselName || '').toLowerCase().includes(q));
    }

    get displayedCaptains(): CaptainRow[] {
        if (this.captainSearchQuery.trim()) return this.filteredRoster;
        return this.filteredRoster.slice(0, MapFilterComponent.CAPTAIN_PREVIEW_COUNT);
    }
    get captainOverflow(): number {
        if (this.captainSearchQuery.trim()) return 0;
        return this.filteredRoster.length - this.displayedCaptains.length;
    }

    get allCaptainsVisible(): boolean {
        if (this.loadingRoster) return this.hiddenCaptainUserIds.size === 0;
        return this.roster.length > 0 && this.roster.every((r) => this.captainChecked(r));
    }
    get someCaptainsVisible(): boolean {
        return this.roster.some((r) => this.captainChecked(r)) && !this.allCaptainsVisible;
    }
    toggleAllCaptains() {
        const hideAll = this.allCaptainsVisible;
        this.roster.forEach((r) => this.setCaptainHidden(r, hideAll, true));
        if (hideAll) {
            this.publicEnabled = false;
            this.fleets.forEach((f) => {
                const cands = this.getFleetIdCandidates(f);
                cands.forEach((id) => this.hiddenGroupIds.add(id));
            });
            this.hiddenGroupIds = new Set(this.hiddenGroupIds);
        }
        this.persist(false);
    }

    get filteredFleets(): any[] {
        const q = this.groupSearchQuery.toLowerCase();
        return q ? this.fleets.filter((f) => (f.name || '').toLowerCase().includes(q)) : this.fleets;
    }

    isFleetVisible(f: any): boolean {
        return !this.getFleetIdCandidates(f).some((id) => this.hiddenGroupIds.has(id));
    }
    toggleFleet(f: any) {
        const cands = this.getFleetIdCandidates(f);
        if (cands.length === 0) return;
        const hide = this.isFleetVisible(f);
        cands.forEach((id) => hide ? this.hiddenGroupIds.add(id) : this.hiddenGroupIds.delete(id));
        if (!hide) cands.forEach((id) => this.autoSeededHiddenGroups.delete(id));
        for (const row of this.roster) {
            if (cands.some((id) => row.groupIds.has(id))) this.setCaptainHidden(row, hide, false);
        }
        this.hiddenGroupIds = new Set(this.hiddenGroupIds);
        this.persist(false);
    }

    get allFleetsVisible(): boolean {
        if (this.loadingFleets) return this.hiddenGroupIds.size === 0;
        return this.fleets.length > 0 && this.fleets.every((f) => this.isFleetVisible(f));
    }
    get someFleetsVisible(): boolean {
        return this.fleets.some((f) => this.isFleetVisible(f)) && !this.allFleetsVisible;
    }
    toggleAllFleets() {
        const turningOff = this.allFleetsVisible;
        this.fleets.forEach((f) => { if (this.isFleetVisible(f) === turningOff) this.toggleFleet(f); });
        this.persist(false);
    }

    togglePublic() {
        this.publicEnabled = !this.publicEnabled;
        const show = this.publicEnabled;
        for (const row of this.roster) {
            if (row.isDockmate || row.groupIds.size > 0) continue;
            this.setCaptainHidden(row, !show, false);
        }
        this.persist(false);
    }
}
